import { ComponentType, ReactNode } from 'react';
import { Database, type LucideProps } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCan } from '../../hooks/useCan';
import { useLoadSampleData } from '../../hooks/useSampleData';
import { useToast } from './Toast';

/**
 * Guided empty state (Wave 5). Replaces dead "No X" strings on the main list
 * pages with: a small icon + one-line "what this is" + a primary CTA. When the
 * *whole workspace* is empty we also offer a "Load sample data" shortcut so a
 * fresh buyer instantly sees "what good looks like".
 *
 * The primary CTA is the page's own create/upload action; it is rendered only
 * when the caller passes one AND the user can mutate (viewers see the
 * description + sample-data shortcut, never a create button).
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  primaryLabel,
  onPrimary,
  primaryIcon: PrimaryIcon,
  offerSampleData = false,
  wsId,
}: {
  icon: ComponentType<LucideProps>;
  title: string;
  description: string;
  /** Primary CTA label (e.g. "Add vendor"). Omit for read-only surfaces. */
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryIcon?: ComponentType<LucideProps>;
  /** When true and the workspace is empty, also show "Load sample data". */
  offerSampleData?: boolean;
  wsId?: string;
}) {
  const { t } = useTranslation();
  const { canMutate } = useCan();
  const toast = useToast();
  const load = useLoadSampleData(wsId);

  const showPrimary = !!primaryLabel && !!onPrimary && canMutate;
  const showSample = offerSampleData && canMutate;

  const onLoadSample = () => {
    load.mutate(undefined, {
      onSuccess: () => toast.success(t('sampleData.loaded')),
      onError: () => toast.error(t('sampleData.error')),
    });
  };

  let actions: ReactNode = null;
  if (showPrimary || showSample) {
    actions = (
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        {showPrimary && (
          <button
            type="button"
            onClick={onPrimary}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 transition-colors"
          >
            {PrimaryIcon && <PrimaryIcon size={15} />}
            {primaryLabel}
          </button>
        )}
        {showSample && (
          <button
            type="button"
            onClick={onLoadSample}
            disabled={load.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ink-300 dark:border-ink-700 text-sm font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Database size={15} />
            {load.isPending ? t('sampleData.loading') : t('emptyState.loadSampleData')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto h-11 w-11 rounded-full bg-accent-50 dark:bg-accent-900/30 flex items-center justify-center mb-3">
        <Icon size={20} className="text-accent-600 dark:text-accent-300" />
      </div>
      <div className="display text-base font-semibold text-ink-900 dark:text-ink-100">{title}</div>
      <p className="mt-1 text-sm text-ink-500 max-w-sm mx-auto leading-relaxed">{description}</p>
      {actions}
    </div>
  );
}
